import { saveAs } from 'file-saver'

import { first, isEmpty } from 'lodash'
import moment from 'moment'
import queryString from 'query-string'
import pluralize from 'pluralize'
import React from 'react'
import { toast } from '@/components/toasts/toast.helpers'

import { apiService, fetchingAPI } from '../../api'
import { getDateRangeFromSavedFilter } from '../../utils/helpers'
import { fetchPlaylists } from '../playlists/playlists.actions'
import { fetchSavedFilters } from '../savedFilters/savedFilters.actions'
import { CALL_DETAIL_PAGE } from '../callSearch/callSearch.actions'
import {
  QA_ACTIVITY_FILTER_TYPE,
  COPILOT_FILTER_TYPE,
} from '../savedFilters/savedFilters.constants'
import { formatScorecardFilters, parseDashboardToCsv } from './helpers'
import { fetchScorecardsByOrg } from './scorecards.actions'
import { setData, setLoading, setFilter, setDateRangeFilter } from './scorecards.redux'

export const fetchCriteriaScoresByMeasureId = (scorecardId, measureId) => async (dispatch) => {
  try {
    dispatch(setLoading('criteriaScoresByMeasure', true))

    const criteriaScoresObject = await fetchingAPI(
      `${apiService.scorecard}/scoring/scores/${scorecardId}/criteria_by_measure/${measureId}`,
      'GET',
      dispatch
    )

    if (criteriaScoresObject) {
      dispatch(setData('criteriaScoresByMeasure', criteriaScoresObject))
    }
  } catch (err) {
    toast.error('Could not fetch criteria scores', { cause: err })
  } finally {
    dispatch(setLoading('criteriaScoresByMeasure', false))
  }
}

export const fetchCriteriaUpdateAuditEventsForOrg =
  (isCopilot = false) =>
  async (dispatch, getState) => {
    try {
      const { scorecards } = getState()
      dispatch(setData('criteriaAuditEvents', []))
      dispatch(setLoading('criteriaAuditEvents', true))
      const filterString = formatScorecardFilters(scorecards.filters, null, isCopilot)
      const criteriaAuditEvents = await fetchingAPI(
        `${apiService.scorecard}/history/criteria_events?${filterString}`,
        'GET',
        dispatch
      )

      if (criteriaAuditEvents) {
        dispatch(setData('criteriaAuditEvents', criteriaAuditEvents))
      }
    } catch (err) {
      toast.error('Could not fetch criteria audit events', { cause: err })
      console.error(err)
    } finally {
      dispatch(setLoading('criteriaAuditEvents', false))
    }
  }

export const fetchManagerAudit = (taskTypes = ['qa_dispute', 'qa_manual_score'], copilot) => {
  return async (dispatch, getState) => {
    dispatch(setLoading('managerAudit', true))

    try {
      const { filters } = getState().scorecards

      const filtersWithTaskTypes = {
        ...filters,
        taskTypes,
      }
      const filterString = formatScorecardFilters(filtersWithTaskTypes, null, copilot)
      const response = await fetchingAPI(
        `${apiService.workflow}/tasks/manager_audit?${filterString}`,
        'GET',
        dispatch
      )
      dispatch(setData('managerAudit', response))
    } catch (err) {
      toast.error('Failed to fetch supervisor audit data', { cause: err })
    } finally {
      dispatch(setLoading('managerAudit', false))
    }
  }
}

export const fetchQAAnalystActivity =
  ({ organizationId, startDate, endDate, scorecardType } = {}) =>
  async (dispatch, getState) => {
    dispatch(setLoading('qaAnalystActivity', true))

    try {
      const { filters } = getState().scorecards
      const orgId = organizationId || filters.organization
      const start = startDate || (filters.startDate && moment(filters.startDate).utc().format())
      const end = endDate || (filters.endDate && moment(filters.endDate).utc().format())
      const scorecardConfigSids = (filters.scorecards || []).map(
        (scorecard) => scorecard.sid || scorecard
      )
      const playlistIds = (filters.playlists || []).map((playlist) => playlist.value || playlist)
      const managerIds = (filters.managerIds || []).map((manager) => manager.value || manager)

      const params = queryString.stringify(
        {
          requested_organization_id: orgId,
          start_date: start,
          end_date: end,
          scorecard_type: scorecardType,
          ...(!isEmpty(scorecardConfigSids) && { scorecard_config_sids: scorecardConfigSids }),
          ...(!isEmpty(playlistIds) && { playlist_ids: playlistIds }),
          ...(!isEmpty(managerIds) && { manager_ids: managerIds }),
        },
        { skipNull: true, arrayFormat: 'none' }
      )
      const response = await fetchingAPI(
        `${apiService.scorecard}/disputes/analyst_activity?${params}`,
        'GET',
        dispatch
      )
      dispatch(setData('qaAnalystActivity', response || []))
    } catch (error) {
      console.error('QA Analyst Activity fetch error:', error)
      toast.error('Failed to fetch QA analyst activity data', { cause: error })
    } finally {
      dispatch(setLoading('qaAnalystActivity', false))
    }
  }

export const fetchCallViewCounts = () => async (dispatch, getState) => {
  dispatch(setLoading('callViewCounts', true))

  try {
    const { filters } = getState().scorecards
    const orgId = filters.organization
    const start = filters.startDate && moment(filters.startDate).utc().format()
    const end = filters.endDate && moment(filters.endDate).utc().format()

    const params = queryString.stringify(
      {
        requested_organization_id: orgId,
        start_date: start,
        end_date: end,
        pages: CALL_DETAIL_PAGE,
      },
      { skipNull: true }
    )

    const response = await fetchingAPI(
      `${apiService.reporting}/page-views/counts?${params}`,
      'GET',
      dispatch
    )

    dispatch(setData('callViewCounts', response || []))
  } catch (error) {
    console.error('Call view counts fetch error:', error)
    toast.error('Failed to fetch call view counts', { cause: error })
  } finally {
    dispatch(setLoading('callViewCounts', false))
  }
}

export const loadQAActivityDefaultFilters = (isCopilot) => async (dispatch, getState) => {
  const filterType = isCopilot ? COPILOT_FILTER_TYPE : QA_ACTIVITY_FILTER_TYPE
  const scorecardType = isCopilot ? 'copilot' : 'automated'

  try {
    await dispatch(fetchSavedFilters(filterType))
    const { savedFilterList } = getState().savedFilters
    const allFilters = savedFilterList[filterType] || []
    const defaultFilter = allFilters.find((filter) => filter.is_default)

    if (defaultFilter?.filters) {
      const filterData = defaultFilter.filters
      dispatch(setFilter('managerIds', filterData.agents || []))
      dispatch(setFilter('scorecards', filterData.scorecards || []))
      dispatch(setFilter('playlists', filterData.playlists || []))

      if (filterData.dateRange) {
        const { startDate, endDate } = getDateRangeFromSavedFilter(filterData.dateRange)
        dispatch(setDateRangeFilter({ startDate, endDate }))
      }
    }

    // Refetch filter options so ViewScoresFilters syncs with applied filter values
    const orgId = getState().scorecards.filters.organization
    if (orgId) {
      await dispatch(fetchScorecardsByOrg(orgId, scorecardType))
      dispatch(fetchPlaylists(orgId, false))
    }

    dispatch(fetchManagerAudit(['qa_dispute', 'qa_manual_score'], isCopilot))
    dispatch(fetchCriteriaUpdateAuditEventsForOrg(isCopilot))
    dispatch(fetchQAAnalystActivity({ scorecardType }))
    dispatch(fetchCallViewCounts())

    return defaultFilter
  } catch (err) {
    console.error('Error loading QA Activity default filters:', err)
    return undefined
  }
}

export const scoreCopilotScorecard =
  (organizationId, callIds = [], scorecardConfigId) =>
  async (dispatch) => {
    const params = queryString.stringify({ requested_organization_id: organizationId })

    try {
      const body = JSON.stringify({
        call_ids: callIds,
        scorecard_config_id: scorecardConfigId,
      })

      await fetchingAPI(
        `${apiService.scorecard}/scoring/scores/copilot/score?${params}`,
        'POST',
        dispatch,
        body
      )
      toast.success(
        <div>
          <p>{`${pluralize('Call', callIds.length)} submitted for scoring. Results will be ready shortly — refresh the page in a few minutes to view them.`}</p>
        </div>
      )
    } catch (err) {
      toast.error(`Failed to Score: ${err.message || err.detail || 'Unknown error'}`, {
        cause: err,
      })
    }
  }

export const fetchQaAuditEventsByCallId = (callId, organizationId) => async (dispatch) => {
  if (!callId || !organizationId) return
  try {
    dispatch(setLoading('qaAuditEvents', true))

    const eventsResponse = await fetchingAPI(
      `${apiService.scorecard}/history/calls/${callId}?requested_organization_id=${organizationId}`,
      'GET',
      dispatch
    )

    dispatch(setData('qaAuditEvents', eventsResponse))
  } catch (err) {
    console.info(`No QA audit events found for call: ${callId}`)
  } finally {
    dispatch(setLoading('qaAuditEvents', false))
  }
}

// CSV

// TODO: double check if this needs to be called instead of the default widget csv functionality?
export const exportScoreDashboardToCsv = (rows, headers) => async (dispatch, getState) => {
  dispatch(setLoading('csv', true))
  const { scorecards } = getState()
  const { data, filters } = scorecards
  const { aggregateFilters, targetScorecardConfig } = data
  const scorecardNameString = first(filters.scorecards).label.replace(/\s/g, '_')
  const dateRangeString = ''.concat(aggregateFilters.start_date, '-', aggregateFilters.end_date)
  const fileName = ''.concat(
    'balto-qa-dashboard-',
    scorecardNameString,
    '-',
    dateRangeString,
    '.csv'
  )

  const userScoreKeys = ['user_full_name', 'overall_score']
  targetScorecardConfig.sections.forEach((section) => {
    userScoreKeys.push(section.name)
  })

  try {
    const response = parseDashboardToCsv(rows, headers)
    saveAs(response, fileName)
  } catch (err) {
    toast.error('Failed to fetch CSV file', { cause: err })
  } finally {
    dispatch(setLoading('csv', false))
  }
}
